import { Renderer2, RendererStyleFlags2 } from '@angular/core';

/**
 * Keeps a dialog on <body> while it is up, so it centres on the SCREEN wherever it was declared.
 *
 * A bootstrap `.modal` is `position: fixed`, and a fixed element centres on its containing block - the
 * viewport only while no ancestor has a transform. The app's slide-in panels all carry one (their slide),
 * so a confirm dialog declared inside a panel centred on the PANEL (reported on the client settings
 * window, 2026-09-25). Moving the element to <body> takes it out of every such ancestor. Angular keeps
 * owning the node - only its DOM parent changes - so bindings and outputs are untouched, and a comment
 * placeholder marks the exact spot for the way back.
 *
 * Leaving its old parent also means leaving the stacking context the dialog was drawn in: a panel stacked
 * at or above the dialog's own z-index would now cover it, so `park` lifts the dialog just over that panel.
 * The same goes for a panel that is open BESIDE the dialog's home rather than around it: the template
 * composer declares its delete confirms next to its Roles and Manage sections panels (1120), and the
 * confirm (1110) opened under the panel it was asked from (user, 2026-09-28). So `park` also lifts the
 * dialog over the highest other dialog on screen.
 *
 * Call `park()` before showing the dialog and `unpark()` once it has hidden (ngx-bootstrap's `onHidden`,
 * so it fades out where it faded in) AND in the owner's `ngOnDestroy`, or a dialog up when its owner goes
 * would stay behind on <body>.
 */
export class BodyParking {
    private placeholder: Comment | null = null;
    private isRaised = false;

    constructor(
        private readonly renderer: Renderer2,
        private readonly doc: Document,
        /** The element that moves. */
        private readonly host: () => HTMLElement | null | undefined,
        /** The `.modal` whose z-index decides what it is drawn over - the host itself unless it wraps one. */
        private readonly dialog: () => HTMLElement | null | undefined = host
    ) { }

    park(): void {
        const host = this.host();
        const home = host?.parentNode;

        if (!host || !home || this.placeholder || home === this.doc.body) {
            return;
        }

        const dialog = this.dialog();
        const level = Math.max(this.stackLevel(home), this.topOpenDialogLevel(host));
        this.placeholder = this.renderer.createComment('vf-body-parking');
        this.renderer.insertBefore(home, this.placeholder, host);
        this.renderer.appendChild(this.doc.body, host);

        const ownLevel = dialog ? parseInt(this.styleOf(dialog).zIndex, 10) || 0 : 0;
        if (dialog && level >= ownLevel) {
            this.renderer.setStyle(dialog, 'z-index', String(level + 10), RendererStyleFlags2.Important);
            this.isRaised = true;
        }
    }

    unpark(): void {
        const host = this.host();
        const placeholder = this.placeholder;
        this.placeholder = null;

        if (this.isRaised) {
            const dialog = this.dialog();
            if (dialog) {
                this.renderer.removeStyle(dialog, 'z-index', RendererStyleFlags2.Important);
            }
            this.isRaised = false;
        }

        if (!host || !placeholder) {
            return;
        }

        const home = placeholder.parentNode;
        if (home) {
            // Back in place - or into its owner's detached subtree when that view is being destroyed.
            this.renderer.insertBefore(home, host, placeholder);
            this.renderer.removeChild(home, placeholder);
        } else if (host.parentNode) {
            this.renderer.removeChild(host.parentNode, host);
        }
    }

    /** The z-index of the outermost positioned ancestor that sets one: the layer the dialog was drawn in. */
    private stackLevel(from: Node): number {
        let level = 0;
        for (let node = from instanceof Element ? from : from.parentElement; node && node !== this.doc.body; node = node.parentElement) {
            const style = this.styleOf(node);
            const zIndex = parseInt(style.zIndex, 10);
            if (!isNaN(zIndex) && style.position !== 'static') {
                level = zIndex;
            }
        }
        return level;
    }

    /**
     * The highest z-index among the other dialogs on screen (`.modal.show`), leaving out the one being
     * parked and anything inside it: a panel open beside the dialog's home covers it just the same.
     */
    private topOpenDialogLevel(host: HTMLElement): number {
        let level = 0;
        const open = this.doc.querySelectorAll('.modal.show');
        for (let i = 0; i < open.length; i++) {
            const el = open[i];
            if (el === host || host.contains(el)) {
                continue;
            }
            const style = this.styleOf(el);
            const zIndex = parseInt(style.zIndex, 10);
            if (style.display !== 'none' && !isNaN(zIndex) && zIndex > level) {
                level = zIndex;
            }
        }
        return level;
    }

    private styleOf(el: Element): CSSStyleDeclaration {
        return (this.doc.defaultView || window).getComputedStyle(el);
    }
}
